import React, { useEffect, useMemo, useState } from "react";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useSnackbar } from "notistack";
import {
  Box,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Modal,
  Tooltip,
  Typography,
  Stack,
} from "@mui/material";
import {
  ForwardToInbox as ForwardIcon,
  InfoOutlined,
  MailOutlined,
  NoteOutlined,
  NotificationsOffOutlined,
  NotificationsOutlined,
  FileDownloadOutlined,
  AddCircleOutlineOutlined as AddIcon,
  EmailOutlined as AddEmailIcon,
  NotificationsOutlined as AddReminderIcon,
  NoteOutlined as AddNoteIcon,
  DraftsOutlined,
  CheckCircleOutlined,
  RadioButtonUncheckedOutlined,
  DeleteOutlined as DeleteIcon,
  MoreVert,
  VisibilityOffOutlined as InternalVisibilityIcon,
  VisibilityOutlined as ExternalVisibilityIcon,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import DataTable from "../DataTable.jsx";
import { activeBrand } from "../../theme/brands/index.js";
import { getDatum, getDatumZeit } from "../../services/formatService.js";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { applyFilterWithAccessorKeys } from "./utils/tableUtils.js";
import { useClaimCommunicationActions } from "../../hooks/useClaimCommunicationActions.jsx";
import { useClaimActions } from "../../hooks/useClaimActions.jsx";
import PropTypes from "prop-types";
import { backendGet, backendDelete } from "../../services/dataService.js";
import { colorsFoundation, colorsSemantic } from "../../theme/index.js";
import { useClaimCommunicationPermissions } from "../../hooks/permissions/useClaimCommunicationPermissions.jsx";
import Button from "../../theme/components/Button.jsx";
import ReminderModal from "../modals/ReminderModal.jsx";

export default function CommunicationsTable({
  schaden_id,
  claim,
  onRowClick = () => {},
  onClickNewCommunication = () => {},
  onClickNewNote = () => {},
  onClickForward = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { canMessageInsurer } = useClaimCommunicationPermissions();
  const {
    fetchTypeCounts,
    typeCounts,
    state: communicationState,
    setState: setCommmunicationState,
    toggleCommunicationInternal,
    toggleCommunicationDone,
  } = useClaimCommunicationActions();

  const {
    canDelete: canDeleteCommunication,
    canForward: canForwardCommunication,
    canRemind: canRemindCommunication,
  } = useClaimCommunicationPermissions();
  const [activeFilterTab, setActiveFilterTab] = useState("all");
  const [columnOrder, setColumnOrder] = useState([]);

  // Reminder an Kommunikation
  const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
  const [communicationToRemind, setCommunicationToRemind] = useState(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [communicationToDelete, setCommunicationToDelete] = useState(null);
  const [isDeletingCommunication, setIsDeletingCommunication] = useState(false);

  const tableKey = "kommunikation";
  const storageKey = "v2_table_kommunikation";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const rows = communicationState?.data || [];

  const tablesConfig = useMemo(() => {
    return [
      {
        key: tableKey,
        label: "Kommunikation",
        route: `/data/kommunikations/${schaden_id}`,
        exportRoute: `/data/kommunikations/${schaden_id}/export`,
        exportFilename: `kommunikation_schaden_${schaden_id}`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "created_at", order: "desc" }],
          include: ["createdBy", "reminder"],
          filters: {
            type_category: ["reminder", "all"].includes(activeFilterTab)
              ? null
              : activeFilterTab,
            has_due_from: activeFilterTab === "reminder" ? true : null,
          },
        },
        onStateChange: (data) => {
          setCommmunicationState({
            ...data,
            meta: {
              ...data.meta,
              ...(communicationState?.meta || {}),
            },
          });
        },
      },
    ];
  }, [
    schaden_id,
    defaultRowsPerPage,
    storageKey,
    activeFilterTab,
    communicationState,
  ]);

  const {
    tableStates,
    updateTableState,
    fetchData,
    exportData,
    tableExportStates,
  } = useMultiTableData(tablesConfig, enqueueSnackbar);

  const tableState = tableStates[tableKey] || {};

  const totalRows = tableState.totalRows ?? rows.length;
  const filterTabs = useMemo(() => {
    return [
      {
        key: "all",
        label: "Alle",
        count: typeCounts?.all ?? 0,
        onClick: setActiveFilterTab,
      },
      {
        key: "message",
        label: "Mitteilungen",
        count: typeCounts?.message ?? 0,
        onClick: setActiveFilterTab,
      },
      {
        key: "note",
        label: "Notizen",
        count: typeCounts?.note ?? 0,
        onClick: setActiveFilterTab,
      },
      {
        key: "reminder",
        label: "Erinnerungen",
        count: typeCounts?.reminder ?? 0,
        onClick: setActiveFilterTab,
      },
    ];
  }, [typeCounts, totalRows]);

  const [rowMenuState, setRowMenuState] = useState({
    anchorEl: null,
    menuRowId: null,
  });

  const handleMenuOpen = (event, id) => {
    setRowMenuState({ anchorEl: event.currentTarget, menuRowId: id });
    event?.stopPropagation();
  };
  const handleMenuClose = (event) => {
    event?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  };

  const handleToggleInternal = async (communication, newInternalState) => {
    await toggleCommunicationInternal(communication, newInternalState, {
      onSuccess: () => {
        enqueueSnackbar(
          newInternalState
            ? "Kommunikation nicht mehr für VN sichtbar"
            : "Kommunikation jetzt für VN sichtbar",
          { variant: "success" }
        );
      },
    });
  };

  const handleRowClick = (kommunikation) => {
    if (!kommunikation?.id) {
      return;
    }

    onRowClick(kommunikation);
  };

  const handleExportExcel = async () => {
    // TODO: Implement Excel export functionality
    await exportData(tableKey);

    // enqueueSnackbar("Excel-Export wird implementiert", { variant: "info" });
  };

  const handleNewCommunication = () => {
    // TODO: Navigate to new communication form or open modal
    onClickNewCommunication();
    // navigate(`/schaden/${schaden_id}/kommunikation/neu`);
  };

  const handleNewNote = () => {
    onClickNewNote();
  };

  const handleOpenReminderDialog = (e, row) => {
    e?.stopPropagation();
    setCommunicationToRemind(row);
    setIsReminderDialogOpen(true);
  };

  const handleCloseReminderDialog = () => {
    setIsReminderDialogOpen(false);
    setCommunicationToRemind(null);
  };

  const handleOpenDeleteDialog = (e, row) => {
    e?.stopPropagation();
    setCommunicationToDelete(row);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeletingCommunication) {
      return;
    }
    setIsDeleteDialogOpen(false);
    setCommunicationToDelete(null);
  };

  const handleDeleteCommunication = async () => {
    if (!communicationToDelete) {
      return;
    }

    setIsDeletingCommunication(true);
    try {
      const response = await backendDelete(
        `/data/kommunikations/${schaden_id}/${communicationToDelete.id}`,
        {
          label: "Kommunikation löschen",
          enqueueSnackbar,
        }
      );

      if (response) {
        enqueueSnackbar("Kommunikation gelöscht", { variant: "success" });
        fetchData(tableKey);
        fetchTypeCounts();
        handleCloseDeleteDialog();
      }
    } finally {
      setIsDeletingCommunication(false);
    }
  };

  const handleToggleReminderDone = async (e, row) => {
    e.stopPropagation();
    const newValue = !row.ist_erledigt;

    await toggleCommunicationDone(row, newValue, {
      onSuccess: () => {
        enqueueSnackbar(
          newValue
            ? "Kommunikation ist als erledigt markiert"
            : "Kommunikation als nicht erledigt markiert",
          { variant: "success" }
        );
        fetchData(tableKey);
        fetchTypeCounts();
      },
    });
  };

  useEffect(() => {
    fetchData(tableKey);
    fetchTypeCounts();
  }, [schaden_id]);

  useEffect(() => {
    // Reset pagination and trigger refetch when filters change for full variant
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
  }, [activeFilterTab, tableKey]);

  const toolbarActions = [
    {
      type: "dropdown-button",
      label: "Neu",
      icon: <AddIcon />,
      options: [
        {
          label: "Neue Notiz",
          icon: <AddNoteIcon />,
          onClick: handleNewNote,
        },
        {
          label: "Neue Mitteilung",
          icon: <AddEmailIcon />,
          onClick: handleNewCommunication,
        },
      ],
    },
    {
      label: "Excel Export",
      type: "icon",
      variant: "tertiary",
      icon: tableExportStates[tableKey].isExporting ? (
        <CircularProgress color="primary" size={32} thickness={5} />
      ) : (
        <FileDownloadOutlined />
      ),
      onClick: handleExportExcel,
      disabled: tableExportStates[tableKey].isExporting,
    },
  ];

  const MESSAGE_TYPES = [
    "system_message",
    "insurer_email",
    "tenant_message",
    "customer_message",
  ];

  const DRAFTABLE_TYPES = [
    "insurer_email",
    "tenant_message",
    "customer_message",
  ];

  const getCommunicationIcons = (row) => {
    const iconProps = { sx: { fontSize: "1.5rem" } };
    let icons = [];

    const isMessageType = MESSAGE_TYPES.includes(row.type);
    const isDraftable = DRAFTABLE_TYPES.includes(row.type);
    const isDraft = isDraftable && !row.sent_at;

    if (isDraft) {
      icons.push(
        <Tooltip title="Entwurf" key="draft">
          <DraftsOutlined
            {...iconProps}
            sx={{ color: colorsFoundation.key.gold[400] }}
          />
        </Tooltip>
      );
    } else if (row.type === "system_message") {
      icons.push(
        <Tooltip title="Systemmeldung" key="system_message">
          <InfoOutlined {...iconProps} />
        </Tooltip>
      );
    } else if (isMessageType) {
      icons.push(
        <Tooltip title="Mitteilung" key="message">
          <MailOutlined {...iconProps} />
        </Tooltip>
      );
    } else if (row.type === "note") {
      icons.push(
        <Tooltip title="Notiz" key="note">
          <NoteOutlined {...iconProps} />
        </Tooltip>
      );
    }

    if (row.intern) {
      icons.push(
        <Tooltip title="intern (für VN nicht sichtbar)" key="intern">
          <InternalVisibilityIcon {...iconProps} />
        </Tooltip>
      );
    }

    if (row.reminder?.faellig_am) {
      if (row.reminder?.ist_erledigt) {
        icons.push(
          <Tooltip title="Erinnerung" key="reminder">
            <NotificationsOffOutlined
              {...iconProps}
              onClick={(e) => {
                handleOpenReminderDialog(e, row);
              }}
            />
          </Tooltip>
        );
      } else {
        icons.push(
          <Tooltip title="Mit Erinnerung" key="reminder">
            <NotificationsOutlined
              {...iconProps}
              onClick={(e) => {
                handleOpenReminderDialog(e, row);
              }}
            />
          </Tooltip>
        );
      }
    }

    return icons;
  };

  // const iconProps = { sx: { fontSize: "1.5rem" } };

  const reminderDoneColumn = {
    accessorKey: "ist_erledigt",
    header: "Erledigt",
    size: 80,
    enableSorting: false,
    enableColumnFilter: false,
    enableColumnHiding: false,
    muiTableBodyCellProps: () => {
      return { align: "center" };
    },
    Cell: (tableCellObject, passedItem) => {
      const row = tableCellObject ? tableCellObject.row.original : passedItem;
      return (
        <Tooltip
          title={
            row.ist_erledigt ? "Als offen markieren" : "Als erledigt markieren"
          }
        >
          <IconButton
            size="small"
            onClick={(e) => handleToggleReminderDone(e, row)}
            sx={{ p: 0.5 }}
          >
            {row.ist_erledigt ? (
              <CheckCircleOutlined
                sx={{
                  fontSize: "1.5rem",
                  color: colorsFoundation.status.positive[600],
                }}
              />
            ) : (
              <RadioButtonUncheckedOutlined
                sx={{ fontSize: "1.5rem", color: "text.secondary" }}
              />
            )}
          </IconButton>
        </Tooltip>
      );
    },
  };
  // const noteIconColumn = {
  //   accessorKey: "icons",
  //   header: "",
  //   size: 90,
  //   enableSorting: false,
  //   enableColumnFilter: false,
  //   enableColumnHiding: false,
  //   Cell: (tableCellObject, passedItem) => {
  //     const iconProps = { sx: { fontSize: "1.5rem" } };
  //     const objectToRender = tableCellObject
  //       ? tableCellObject.row.original
  //       : passedItem;
  //     return (
  //       <div>
  //         {objectToRender.intern ? (
  //           <InternalVisibilityIcon {...iconProps} />
  //         ) : null}
  //       </div>
  //     );
  //   },
  // };

  const columns = useMemo(
    () => [
      // Conditionally add the "done" column when viewing reminders
      // ...(activeFilterTab === "reminder"
      //   ? [reminderDoneColumn]
      //   : [
      {
        accessorKey: "icons",
        header: "",
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        muiTableBodyCellProps: () => {
          return { align: "left" };
        },
        Cell: (tableCellObject, passedItem) => {
          const objectToRender = tableCellObject
            ? tableCellObject.row.original
            : passedItem;
          return <div>{getCommunicationIcons(objectToRender)}</div>;
        },
      },
      // ]),
      {
        accessorKey: "betreff",
        header: "Betreff",
        size: 350,
        Cell: (tableCellObject, passedItem) => {
          const objectToRender = tableCellObject
            ? tableCellObject.row.original
            : passedItem;

          const isDraftable = DRAFTABLE_TYPES.includes(objectToRender.type);
          const isDraft = isDraftable && !objectToRender.sent_at;
          const { type } = objectToRender;
          const isMessage = [
            "tenant_message",
            "customer_message",
            "insurer_email",
          ].includes(type);

          return (
            <>
              {isDraft && (
                <Tooltip title="Entwurf">
                  <Chip
                    label="Entwurf"
                    size="small"
                    sx={{
                      bgcolor: colorsFoundation.key.gold[400],
                      color: "white",
                    }}
                  />
                </Tooltip>
              )}

              <div>{objectToRender.betreff}</div>

              {isMessage && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  color={colorsSemantic.text.subtle}
                >
                  <Box>An:</Box>
                  {type === "tenant_message" && (
                    <Box mr={2} mb={1}>
                      {claim.kunde.name1}
                    </Box>
                  )}

                  {type === "customer_message" && (
                    <Box mr={2} mb={1}>
                      {activeBrand.labels?.tenantName}
                    </Box>
                  )}

                  {type === "insurer_email" && (
                    <Box mr={2} mb={1}>
                      {claim.vertrag.gesellschaft.name}
                    </Box>
                  )}
                </Stack>
              )}
            </>
          );
        },
      },
      {
        accessorKey: "creator",
        accessorFn: (row) =>
          row.created_by
            ? `${row.created_by?.vorname} ${row.created_by?.nachname}`
            : "Posteingang",
        header: "Erstellt von",
        size: 100,
        Cell: (tableCellObject, passedItem) => {
          const objectToRender = tableCellObject
            ? tableCellObject.row.original
            : passedItem;
          return (
            <span>
              {objectToRender.type === "system_message"
                ? "- - -"
                : objectToRender.created_by
                  ? `${objectToRender.created_by?.vorname} ${objectToRender.created_by?.nachname}`
                  : "Posteingang"}
            </span>
          );
        },
      },
      {
        accessorKey: "faellig_am",
        header: "Erinnern am",
        size: 100,
        enableColumnFilter: false,
        Cell: (tableCellObject, passedItem) => {
          const objectToRender = tableCellObject
            ? tableCellObject.row.original
            : passedItem;
          return (
            <Box
              sx={{ "&:hover": { textDecoration: "underline" } }}
              onClick={(e) => {
                handleOpenReminderDialog(e, objectToRender);
              }}
            >
              {getDatum(objectToRender?.reminder?.faellig_am)}
            </Box>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Erstellt am",
        size: 120,
        enableColumnFilter: false,
        Cell: (tableCellObject, passedItem) => {
          const objectToRender = tableCellObject
            ? tableCellObject.row.original
            : passedItem;
          return <span>{getDatumZeit(objectToRender?.created_at)}</span>;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        Cell: (tableCellObject, passedItem) => {
          const row = tableCellObject
            ? tableCellObject.row.original
            : passedItem;

          const internalToggleable = [
            "insurer_email",
            "system_message",
            "tenant_message",
          ];
          const draftable = ["insurer_email", "tenant_message"];
          const isSentOrNotDraftable =
            row.sent_at || !draftable.includes(row.type);
          const canToggleVisibility =
            canMessageInsurer(claim) &&
            internalToggleable.includes(row.type) &&
            isSentOrNotDraftable;

          const items = [];
          if (canToggleVisibility && row.intern) {
            items.push(
              <MenuItem
                onClick={(e) => {
                  handleMenuClose(e);
                  handleToggleInternal(row, false);
                }}
              >
                <ExternalVisibilityIcon
                  style={{
                    marginRight: "0.5rem",
                  }}
                />
                Für VN freigeben
              </MenuItem>
            );
          } else if (canToggleVisibility && !row.intern) {
            items.push(
              <MenuItem
                onClick={(e) => {
                  handleMenuClose(e);
                  handleToggleInternal(row, true);
                }}
              >
                <InternalVisibilityIcon
                  style={{
                    marginRight: "0.5rem",
                  }}
                />
                Für VN nicht sichtbar machen
              </MenuItem>
            );
          }

          if (canForwardCommunication(row)) {
            items.push(
              <MenuItem
                onClick={(e) => {
                  handleMenuClose(e);
                  onClickForward(e, row);
                }}
              >
                <ForwardIcon
                  style={{
                    marginRight: "0.5rem",
                  }}
                />
                Weiterleiten
              </MenuItem>
            );
          }

          if (canRemindCommunication(row)) {
            items.push(
              <MenuItem
                onClick={(e) => {
                  handleMenuClose();
                  handleOpenReminderDialog(e, row);
                }}
              >
                <NotificationsOutlined
                  style={{
                    marginRight: "0.5rem",
                  }}
                />
                Erinnern
              </MenuItem>
            );
          }

          if (canDeleteCommunication(row)) {
            items.push(
              <MenuItem
                onClick={(e) => {
                  handleMenuClose();
                  handleOpenDeleteDialog(e, row);
                }}
              >
                <DeleteIcon
                  style={{
                    marginRight: "0.5rem",
                  }}
                />
                Löschen
              </MenuItem>
            );
          }

          return (
            items.length > 0 && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, row.id)}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={rowMenuState.anchorEl}
                  open={
                    Boolean(rowMenuState.anchorEl) &&
                    rowMenuState.menuRowId === row.id
                  }
                  onClose={handleMenuClose}
                >
                  {...items}
                </Menu>
              </>
            )
          );
        },
      },
    ],
    [activeFilterTab, rowMenuState]
  );

  // keep column order of dynamic columns
  useEffect(() => {
    setColumnOrder((prev) => {
      return columns.map((col) => col.id || col.accessorKey);
    });
  }, [columns]);

  return (
    <>
      <DataTable
        variant="enclosed"
        columns={columns}
        data={rows}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        filterTabs={filterTabs}
        filterTabsProps={{ variant: "light" }}
        activeFilterTab={activeFilterTab}
        onChangePage={(pagination) => {
          const next =
            typeof pagination === "function"
              ? pagination({
                  pageIndex: tableState.pageIndex,
                  pageSize: tableState.pageSize,
                })
              : pagination;
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangeFilters={(filters) =>
          updateTableState(tableKey, {
            filters: applyFilterWithAccessorKeys(filters, columns),
          })
        }
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        storageKey={storageKey}
        enableHeader
        enableSorting
        manualFiltering={true}
        enableGlobalFilter={true}
        enableColumnFilters={true}
        enableColumnHiding={true}
        topToolbarIcons={true}
        toolbarActions={toolbarActions}
        onRowClick={handleRowClick}
        columnOrder={columnOrder}
        // initialState={{ showGlobalFilter: true }}
      />
      <Modal open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Kommunikation löschen
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Möchten Sie diese Kommunikation wirklich löschen?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              variant="tertiary"
              disabled={isDeletingCommunication}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDeleteCommunication}
              variant="primary"
              disabled={isDeletingCommunication}
              startIcon={<DeleteIcon />}
            >
              {isDeletingCommunication ? "Wird gelöscht..." : "Löschen"}
            </Button>
          </Box>
        </Box>
      </Modal>
      <ReminderModal
        open={isReminderDialogOpen}
        onClose={handleCloseReminderDialog}
        onSubmit={() => {
          fetchTypeCounts();
          fetchData(tableKey);
        }}
        kommunikation_id={communicationToRemind?.id}
        schaden_id={communicationToRemind?.schaden_id}
        initialEditMode={true}
      />
    </>
  );
}

CommunicationsTable.propTypes = {
  schaden_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onRowClick: PropTypes.func,
  onClickNewCommunication: PropTypes.func,
  onClickNewNote: PropTypes.func,
  onClickNewReminder: PropTypes.func,
};
